import React, { useState } from "react";
import PricingRequestModal from "./PricingRequestModal";

// High-fashion pricing section with plan entitlements
// Note: Caps reflect user-preferred defaults captured in project memory.
// Studio / Atelier / Couture (individual) and Pro / Business (per-client)

const plans = [
  {
    code: "studio",
    name: "Studio",
    price: "$6",
    cadence: "mo",
    highlight: false,
    blurb: "Elegant essentials for personal wardrobes.",
    entitlements: [
      { label: "Outfits", value: "up to 20" },
      { label: "Closet items", value: "up to 30" },
      { label: "AI outfit generations", value: "standard" },
      { label: "Image quality", value: "optimized" },
    ],
  },
  {
    code: "atelier",
    name: "Atelier",
    price: "$12",
    cadence: "mo",
    highlight: true,
    blurb: "Our most-loved tier for refined personal style.",
    entitlements: [
      { label: "Outfits", value: "up to 50" },
      { label: "Closet items", value: "up to 60" },
      { label: "AI outfit generations", value: "enhanced" },
      { label: "Image quality", value: "high" },
      { label: "Capsule planning", value: "included" },
    ],
  },
  {
    code: "couture",
    name: "Couture",
    price: "$24",
    cadence: "mo",
    highlight: false,
    blurb: "For the connoisseur: expansive, elevated expression.",
    entitlements: [
      { label: "Outfits", value: "up to 80" },
      { label: "Closet items", value: "up to 100" },
      { label: "AI outfit generations", value: "premium" },
      { label: "Image quality", value: "ultra" },
      { label: "Priority image queue", value: "yes" },
    ],
  },
];

const professional = [
  {
    code: "pro",
    name: "Pro",
    price: "$29",
    cadence: "mo",
    highlight: false,
    blurb: "Independent stylists and creators.",
    footnote: "Per-client caps. Totals align with client count.",
    entitlements: [
      { label: "Clients", value: "flex" },
      { label: "Outfits per client", value: "up to 40" },
      { label: "Closet items per client", value: "up to 60" },
      { label: "AI outfit generations", value: "enhanced" },
      { label: "Client portals", value: "included" },
    ],
  },
  {
    code: "business",
    name: "Business",
    price: "$59",
    cadence: "mo",
    highlight: false,
    blurb: "Studios and boutique teams.",
    footnote: "Per-client caps. Totals align with client count.",
    entitlements: [
      { label: "Clients", value: "scaled" },
      { label: "Outfits per client", value: "up to 60" },
      { label: "Closet items per client", value: "up to 80" },
      { label: "AI outfit generations", value: "premium" },
      { label: "Team collaboration", value: "included" },
    ],
  },
];

const AddOn: React.FC = () => (
  <div className="mt-8 text-center text-xs font-light tracking-wide text-gray-600 dark:text-gray-300">
    Closet expansion add‑ons: <span className="italic">+20</span> items for{" "}
    <span className="italic">$3</span>, <span className="italic">+50</span>{" "}
    items for <span className="italic">$6</span>.
  </div>
);

const Card: React.FC<{
  name: string;
  price: string;
  cadence: string;
  highlight?: boolean;
  blurb?: string;
  entitlements: { label: string; value: string }[];
  footnote?: string;
  cta?: string;
  onCtaClick?: () => void;
}> = ({
  name,
  price,
  cadence,
  highlight,
  blurb,
  entitlements,
  footnote,
  cta = "Request Access",
  onCtaClick,
}) => {
  return (
    <div
      className={
        "relative group rounded-md border border-gray-200/80 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/50 backdrop-blur-sm overflow-hidden transition-all duration-500 " +
        (highlight
          ? "ring-1 ring-pink-300/70 dark:ring-pink-500/40 shadow-[0_0_0_1px_inset_rgba(236,72,153,0.15)]"
          : "hover:shadow-lg")
      }
    >
      {highlight && (
        <div className="absolute top-3 right-4 z-10 text-[10px] uppercase tracking-[0.25em] text-pink-700 dark:text-pink-300 bg-pink-100/70 dark:bg-pink-900/30 px-2 py-1 rounded">
          Most Popular
        </div>
      )}

      <div className="p-8">
        <h3 className="uppercase text-sm font-light tracking-[0.35em] text-gray-800 dark:text-gray-200">
          {name}
        </h3>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-4xl font-light text-gray-900 dark:text-white">
            {price}
          </span>
          <span className="text-xs font-light tracking-wider text-gray-500 dark:text-gray-400">
            /{cadence}
          </span>
        </div>
        {blurb && (
          <p className="mt-4 text-sm font-light leading-relaxed text-gray-600 dark:text-gray-300">
            {blurb}
          </p>
        )}

        <div className="mt-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />

        <ul className="mt-6 space-y-3">
          {entitlements.map((e, idx) => (
            <li
              key={idx}
              className="flex items-center justify-between text-sm font-light tracking-wide text-gray-700 dark:text-gray-200"
            >
              <span className="opacity-80">{e.label}</span>
              <span className="text-gray-900 dark:text-white">{e.value}</span>
            </li>
          ))}
        </ul>

        {footnote && (
          <p className="mt-4 text-[11px] font-light tracking-wide text-gray-500 dark:text-gray-400">
            {footnote}
          </p>
        )}

        <button
          type="button"
          onClick={onCtaClick}
          className="mt-8 w-full uppercase text-xs tracking-[0.3em] font-light text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 py-2.5 rounded hover:scale-[1.02] transition-all duration-300 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900"
        >
          {cta}
        </button>
      </div>

      {/* Decorative gradient lines */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pink-300/60 to-transparent opacity-40" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent opacity-30" />
    </div>
  );
};

const SectionHeader: React.FC<{ title: string; subtitle: string }> = ({
  title,
  subtitle,
}) => (
  <div className="max-w-3xl mx-auto text-center">
    <h2 className="uppercase text-xs tracking-[0.45em] text-gray-700 dark:text-gray-300 font-light">
      {subtitle}
    </h2>
    <p className="mt-4 text-3xl md:text-4xl font-extralight tracking-[0.08em] text-gray-900 dark:text-white">
      {title}
    </p>
  </div>
);

const HomePricing: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{
    code: string;
    name: string;
  } | null>(null);

  const handleCtaClick = (code: string, name: string) => {
    setSelectedPlan({ code, name });
    setModalOpen(true);
  };

  return (
    <section className="relative py-24 px-6">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900" />

      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title="Memberships for Every Wardrobe"
          subtitle="Pricing"
        />

        {/* Individual plans */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <Card
              key={p.code}
              {...p}
              onCtaClick={() => handleCtaClick(p.code, p.name)}
            />
          ))}
        </div>
        <AddOn />

        {/* Professional plans */}
        <div className="mt-20">
          <SectionHeader
            title="For Stylists and Studios"
            subtitle="Professional"
          />
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {professional.map((p) => (
              <Card
                key={p.code}
                {...p}
                cta="Contact Sales"
                onCtaClick={() => handleCtaClick(p.code, p.name)}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedPlan && (
        <PricingRequestModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          planCode={selectedPlan.code}
          planName={selectedPlan.name}
        />
      )}
    </section>
  );
};

export default HomePricing;
